import { useEffect, useState } from "react";
import { Copy, Key, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";

export default function ApiKeysTab({ admin = true }) {
    const [keys, setKeys] = useState(null);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const [delTarget, setDelTarget] = useState(null);
    const [confirmId, setConfirmId] = useState(null);

    const load = () => api.get("/admin/api-keys").then((r) => setKeys(r.data)).catch(() => setKeys([]));
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!name.trim()) return toast.error("Anahtar adı girin (örn: ataangora.com.tr)");
        setBusy(true);
        try {
            const r = await api.post("/admin/api-keys", { name: name.trim() });
            setCreated(r.data);
            setName("");
            load();
            toast.success("API anahtarı oluşturuldu — şimdi kopyalayın, bir daha gösterilmez");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (pin) => {
        try {
            await api.post(`/admin/api-keys/${pinTarget.key_id}/revoke`, {}, { headers: { "X-Critical-Pin": pin } });
            toast.success("Anahtar iptal edildi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İptal edilemedi");
        }
    };

    const directRevoke = async (k) => {
        setBusy(true);
        try {
            await api.post(`/admin/api-keys/${k.key_id}/revoke`);
            toast.success("Anahtar iptal edildi");
            setConfirmId(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İptal edilemedi");
        } finally {
            setBusy(false);
        }
    };

    const hardDelete = async (pin) => {
        try {
            await api.delete(`/admin/records/api_keys/${delTarget.key_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Anahtar kalıcı olarak silindi");
            setDelTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-apikeys-tab">
            <p className="text-sm text-muted-foreground">
                Harici uygulamaların platforma bağlanması için kimlik bilgileri (API Key + API Secret + Merchant ID).
                Salt-okunur katalog: <span className="font-mono">X-API-Key</span> başlığıyla <span className="font-mono">GET /api/public/catalog</span>.
                İmzalı erişim (HMAC-SHA256): <span className="font-mono">/api/v1/ping</span>, <span className="font-mono">/api/v1/products</span>, <span className="font-mono">/api/v1/categories</span> uçlarına
                <span className="font-mono"> X-API-Key, X-Merchant-ID, X-Timestamp, X-Request-ID, X-Signature</span> başlıklarıyla bağlanılır.
            </p>

            <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-card p-4">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anahtar adı (örn: ataangora.com.tr)" className="h-9 w-72" data-testid="apikey-name-input" />
                <Button size="sm" onClick={create} disabled={busy} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="apikey-create-button">
                    <Plus className="mr-1.5 h-4 w-4" /> {busy ? "Oluşturuluyor..." : "Anahtar Oluştur"}
                </Button>
            </div>

            {created && (
                <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="apikey-created-card">
                    <p className="text-sm font-semibold text-emerald-800">Yeni kimlik bilgileri — yalnızca bir kez gösterilir, hemen kaydedin:</p>
                    {[
                        ["Merchant ID", created.merchant_id, "apikey-created-merchant"],
                        ["API Key", created.api_key, "apikey-created-value"],
                        ["API Secret", created.api_secret, "apikey-created-secret"],
                    ].map(([label, value, tid]) => (
                        <div key={tid} className="mt-2 flex items-center gap-2">
                            <span className="w-24 shrink-0 text-xs font-semibold text-emerald-900">{label}</span>
                            <code className="flex-1 overflow-x-auto rounded-lg border bg-white px-3 py-2 font-mono text-xs" data-testid={tid}>{value}</code>
                            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} kopyalandı`); }} data-testid={`${tid}-copy`}>
                                <Copy className="mr-1.5 h-3.5 w-3.5" /> Kopyala
                            </Button>
                        </div>
                    ))}
                    <p className="mt-2 text-[11px] text-emerald-700">API Secret veritabanında şifreli saklanır ve bir daha gösterilmez. İmza: <span className="font-mono">HMAC_SHA256(secret, METHOD + "\n" + PATH + "\n" + MERCHANT_ID + "\n" + TIMESTAMP + "\n" + REQUEST_ID + "\n" + SHA256(body))</span> — hex, küçük harf.</p>
                </div>
            )}

            {!keys ? (
                <div className="h-40 animate-pulse rounded-2xl bg-muted" />
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <table className="w-full text-sm" data-testid="apikeys-table">
                        <thead>
                            <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-3">Ad</th><th className="p-3">Anahtar</th><th className="p-3">Merchant ID</th><th className="p-3">Oluşturma</th><th className="p-3">Son Kullanım</th><th className="p-3">Durum</th><th className="p-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => (
                                <tr key={k.key_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`apikey-row-${k.key_id}`}>
                                    <td className="p-3 font-semibold">{k.name}</td>
                                    <td className="p-3 font-mono text-xs text-muted-foreground">{k.prefix}…</td>
                                    <td className="p-3 font-mono text-xs text-muted-foreground" data-testid={`apikey-merchant-${k.key_id}`}>{k.merchant_id || "—"}</td>
                                    <td className="p-3 text-xs text-muted-foreground">{fmtDate(k.created_at)}</td>
                                    <td className="p-3 text-xs text-muted-foreground">{k.last_used_at ? fmtDate(k.last_used_at) : "—"}</td>
                                    <td className="p-3">
                                        <Badge variant="outline" className={`text-[10px] ${k.active ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`}>
                                            {k.active ? "Aktif" : "İptal"}
                                        </Badge>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            {k.active && (admin ? (
                                                <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setPinTarget(k)} data-testid={`apikey-revoke-${k.key_id}`}>
                                                    <Trash2 className="mr-1 h-3 w-3" /> İptal Et
                                                </Button>
                                            ) : confirmId === k.key_id ? (
                                                <>
                                                    <Button size="sm" variant="outline" className="h-8 border-red-300 bg-red-50 text-xs text-red-700" disabled={busy} onClick={() => directRevoke(k)} data-testid={`apikey-revoke-confirm-${k.key_id}`}>Evet, İptal Et</Button>
                                                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setConfirmId(null)} data-testid={`apikey-revoke-cancel-${k.key_id}`}>Vazgeç</Button>
                                                </>
                                            ) : (
                                                <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setConfirmId(k.key_id)} data-testid={`apikey-revoke-${k.key_id}`}>
                                                    <Trash2 className="mr-1 h-3 w-3" /> İptal Et
                                                </Button>
                                            ))}
                                            {admin && (
                                                <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setDelTarget(k)} data-testid={`apikey-delete-${k.key_id}`}>
                                                    Kalıcı Sil
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!keys.length && (
                                <tr><td colSpan={7} className="p-16 text-center text-sm text-muted-foreground" data-testid="apikeys-empty">
                                    <Key className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Henüz API anahtarı yok.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {admin && (
                <CriticalPinDialog
                    open={!!pinTarget}
                    onClose={() => setPinTarget(null)}
                    title="API Anahtarını İptal Et"
                    message={pinTarget ? `"${pinTarget.name}" anahtarı kalıcı olarak devre dışı bırakılacak. Bu anahtarı kullanan bağlantılar (örn. ataangora.com.tr) anında erişimini kaybeder.` : ""}
                    onSubmit={revoke}
                />
            )}

            {admin && (
                <CriticalPinDialog
                    open={!!delTarget}
                    onClose={() => setDelTarget(null)}
                    title="API Anahtarını Kalıcı Sil"
                    message={delTarget ? `"${delTarget.name}" anahtarı kaydı tamamen silinecek (iptalden farklı olarak kayıt kalmaz).` : ""}
                    onSubmit={hardDelete}
                />
            )}
        </div>
    );
}
